import { Formik, Form, FieldArray } from "formik"
import React, {
  useEffect,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import {
  BaseSelectedValue,
} from "components/Select/Select"
import { handleToast } from "utils/messages"
import { history } from "utils/routes"
import useStateGetter from "hooks/useStateGetter"
import { Company } from "../../../companies/types"
import { fetchCountries } from "../../../countries/actions"
import {
  createConverterSubmissionsOutput, fetchSelectedCompany,
} from "../../actions"
import { SubmissionConverterArrayOutputValidationShema } from "./SubmissionConverterOutputValidationShema";
import { getPrevYear } from "modules/submissions/utils"
import SubmissionConverterNewOutputFormFields from "./SubmissionConverterNewOutputFormFields"
import { getPlants } from "modules/companies/service/api"
import { windowStorage } from "utils/windowStorage"
import { SELECTED_COMPANY_KEY } from "modules/submissions/constants"

/* Set date to -1 month in form */
const actualDate = new Date()
actualDate.setMonth(actualDate.getMonth() - 1)

export type SubmissionType = {
  company: BaseSelectedValue<number> | null
  product: any
  materialGroup: BaseSelectedValue<number> | null
  date: Date
  total: number
  totalPercentageOfRecycledPlasticsInFrance: number
  estimatedShareExportedOutsideEu: number
  estimatedShareExportedInsideEu: number
  totalPet?: number
  totalUnits?: number
  yearlySubmission?: boolean
  originFromEu?: boolean
  productsInFrance?: boolean
}


const INITIAL_FORM_VALUES: SubmissionType = {
  company: null,
  product: null,
  date: actualDate,
  materialGroup: null,
  total: 0,
  totalPet: 0,
  totalUnits: 0,
  totalPercentageOfRecycledPlasticsInFrance: 0,
  estimatedShareExportedOutsideEu: 0,
  estimatedShareExportedInsideEu: 0,
  productsInFrance: false
}

const SubmissionConverterFormsNewOutput: React.FC<{}>  = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  
  const [loading, setLoading] = useState(false)
  const [plants, setPlants] = useState<Company[]>([])
  const selectedComapny =
    useStateGetter<any>(["submission", "selectedCompany"]) ?? undefined
  
  const clearSelectedCompany = () => {
    windowStorage.remove(SELECTED_COMPANY_KEY, false)
    dispatch(fetchSelectedCompany())
  }
  useEffect(() => {    
    dispatch(fetchCountries())
    getPlants().then((d) => {
      const fetchedPlants = d.data
      if (fetchedPlants) {
        setPlants(fetchedPlants)
      }
      if (d.errors) {
        handleToast(d.errors, "error")
      }
    })
  }, [dispatch])


  return (
    <div>
      <Formik
       initialValues={{ submissions: [INITIAL_FORM_VALUES] }}
       validationSchema={SubmissionConverterArrayOutputValidationShema}
       onSubmit={async (values) => {
        if (!loading) {
          setLoading(true)
          console.log(values.submissions);
          
          const promises = values.submissions.map((submission, index) => {
            const companyId = submission.company ? submission.company.value : 0
            const materialsGroupId = submission.materialGroup?.value

            const isEstimatedShareWithingRange =
              submission.estimatedShareExportedOutsideEu + submission.estimatedShareExportedInsideEu <= 100

            if (!isEstimatedShareWithingRange) {
              setLoading(false)
              dispatch(
                handleToast(
                  [
                    "The total value of recycled plastic used in plastic exported to the UK, Norway, Switzerland and outside the EU can only be lower than or equal to 100%.",
                  ],
                  "error",
                ),
              )
            }
            const date = submission.date
            if (submission.yearlySubmission) {
              date.setFullYear(getPrevYear())
              date.setMonth(11)
            }

            if (companyId && isEstimatedShareWithingRange ) {
              const credentails = {
                companyId,
                materialsGroupId,
                collectionDate: date,
                isInput: false,
                type: 1,
                volumeNet: submission.total,
                totalPercentageOfRecycledPlasticsInFrance:
                submission.totalPercentageOfRecycledPlasticsInFrance,
                estimatedShareExportedOutsideEu: submission.estimatedShareExportedOutsideEu,
                estimatedShareExportedInsideEu: submission.estimatedShareExportedInsideEu,
                productId: submission.product === null ? 0 : submission.product.value,
                totalPet: submission.totalPet,
                totalUnits: submission.totalUnits,
                yearlySubmission: submission.yearlySubmission ?? false,
                productsInFrance: submission.productsInFrance ?? false,
                successCallback: () => {
                },
              }
              return dispatch(createConverterSubmissionsOutput(credentails))
            }
          
          })
          await Promise.all(promises)
          
          history.push("/submissions/new-converting", {
            title: t("Converting Activity"),
            tab: "convertingOutput",
          })
          clearSelectedCompany()
          setLoading(false)
        }
      }}
      >
      {({ values }) => (
        <Form>
          <div className="mb-5 clearfix">
            <h2>{t("Output from converting activity")}</h2>
            <Link
              className={
                selectedComapny
                  ? "btn btn-outline-primary float-right"
                  : "disabled-link btn btn-outline-primary float-right"
              }
              to="/used-in"
            >
              {t("Add new product")}
            </Link>

          </div>
          <FieldArray
            name="submissions"
            render={arrayHelpers => (
              <>
              {values.submissions && values.submissions.length && (
                values.submissions.map((submission, index) => {
                  return (
                    <>
                    <SubmissionConverterNewOutputFormFields submission={submission} plants={plants} index={index}/>
                    <div className="row my-4">                        
                      <div className="col-sm-9 col-md-8 col-lg-7">
                        <button
                          type="button"
                          className="btn btn-success-light rounded-bottom-left"
                          onClick={() => {
                            arrayHelpers.insert(index+1, {...INITIAL_FORM_VALUES, 
                              company: submission.company,
                              date: submission.date, 
                              yearlySubmission: !!submission.yearlySubmission
                            })
                          }}
                        >
                          <i className="fas fa-plus mr-3" />
                          {t("Add new Submission")}
                        </button>
                      </div>
                      <div className="col-sm-3 col-md-4 col-lg-1">
                        {index !== 0 && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-primary rounded-bottom-left"
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            {t("Cancel")}
                          </button>
                          )}
                      </div>
                    </div>
                    </>
                  )                  
                })
              )}

              </>
            )}
          />
          <button
            type="submit"
            className="btn btn-success float-right rounded-bottom-right ml-2"
            disabled={loading}
          >
            {t("Save")}
            <i className="fas fa-arrow-right ml-3" />
          </button>
        </Form>
      )}
      </Formik>
    </div>
  )
}

export default SubmissionConverterFormsNewOutput
